import {useCallback, useState, useEffect} from 'react';
import type {CSSProperties} from 'react';

import classnames from 'classnames';

import styles from './illustration.module.css';

interface IllustrationProps {
    src?: string;
    width?: number | string;
    height?: number | string;
    alt?: string;
    loading?: 'lazy' | 'eager';
    fullPath?: string;
}

const SLOW_LOAD_THRESHOLD_MS = 150;

export function Illustration(props: IllustrationProps) {
    const {src, width, height, alt = '', loading = 'lazy'} = props;

    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    // If image takes longer than SLOW_LOAD_THRESHOLD_MS to load (slow
    // connection), we want to animate it in for a smoother transition
    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldAnimate(true);
        }, SLOW_LOAD_THRESHOLD_MS);

        return () => clearTimeout(timer);
    }, []);

    const handleLoad = useCallback(() => {
        requestAnimationFrame(() => {
            setIsLoaded(true);
        });
    }, []);

    const handleError = useCallback(() => {
        setHasError(true);
    }, []);

    const styleObj: CSSProperties = {};
    if (typeof width === 'string') {
        styleObj.width = width;
        if (!height) styleObj.height = 'auto';
    }

    if (typeof height === 'string') {
        styleObj.height = height;
        if (!width) styleObj.width = 'auto';
    }

    const illustrationSrc = props.fullPath ? props.fullPath : `/include/illustrations/${src}.svg`;

    if (hasError) {
        if (process.env.NODE_ENV !== 'production') {
            const path = props.fullPath ?? `/include/illustrations/${src}.svg`;
            throw new Error(`Illustration: Failed to load image at "${path}".`);
        }

        return null;
    }

    return (
        <img
            src={illustrationSrc}
            width={width}
            height={height}
            alt={alt}
            loading={loading}
            style={styleObj}
            onLoad={handleLoad}
            onError={handleError}
            className={classnames({
                [styles['illustration-animate']]: shouldAnimate && !isLoaded,
                [styles['illustration-loaded']]: isLoaded,
            })}
        />
    );
}
